import { Layers, Plus } from 'lucide-react'

export type FloorGalleryItem = {
  id: string
  name: string
  widthM: number
  heightM: number
  hasBackground?: boolean
}

type Props = {
  floors: FloorGalleryItem[]
  selectedFloorId: string
  thumbnails: Record<string, string | null | undefined>
  loadingThumbnails?: boolean
  onSelect: (floorId: string) => void
  onAddFloor?: () => void
}

export default function FloorPlanFloorGallery({
  floors,
  selectedFloorId,
  thumbnails,
  loadingThumbnails,
  onSelect,
  onAddFloor,
}: Props) {
  if (floors.length === 0) return null

  const selected = floors.find(f => f.id === selectedFloorId)

  return (
    <div className="floor-plan-gallery shrink-0 overflow-hidden">
      <div className="flex items-center justify-between gap-3 px-3 py-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <Layers size={14} className="shrink-0 text-mg-accent" aria-hidden />
            <span className="text-xs font-semibold text-mg-text">Floors</span>
            <span className="rounded-full bg-mg-border/40 px-2 py-0.5 text-[10px] font-semibold tabular-nums text-mg-muted">
              {floors.length}
            </span>
          </div>
          {selected && (
            <p className="mt-0.5 truncate text-[11px] text-mg-muted" title={selected.name}>
              Editing <span className="font-medium text-mg-text">{selected.name}</span>
              <span className="text-mg-muted"> · {selected.widthM}×{selected.heightM} m</span>
            </p>
          )}
        </div>
        {onAddFloor && (
          <button
            type="button"
            onClick={onAddFloor}
            className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-mg-border bg-mg-surface-2 px-2.5 py-1.5 text-[11px] font-semibold text-mg-text transition-colors hover:border-mg-accent hover:bg-mg-accent-soft hover:text-mg-accent"
          >
            <Plus size={13} />
            Add floor
          </button>
        )}
      </div>

      <div className="relative px-2 py-2.5">
        <div
          className="flex gap-2.5 overflow-x-auto scroll-smooth pb-1 [scrollbar-width:thin]"
          role="tablist"
          aria-label="Floor gallery"
        >
          {floors.map(floor => {
            const isSelected = floor.id === selectedFloorId
            const thumb = thumbnails[floor.id]
            const showImage = typeof thumb === 'string' && thumb.length > 0

            return (
              <button
                key={floor.id}
                type="button"
                role="tab"
                aria-selected={isSelected}
                title={`${floor.name} · ${floor.widthM}×${floor.heightM} m`}
                onClick={() => onSelect(floor.id)}
                className={`group relative flex min-w-[6.75rem] max-w-[10.5rem] flex-1 flex-col overflow-hidden rounded-lg border text-left transition-all duration-150 ${
                  isSelected
                    ? 'border-mg-accent bg-mg-accent-soft shadow-md shadow-mg-accent/10 ring-1 ring-mg-accent/30'
                    : 'border-mg-border/70 bg-mg-page hover:-translate-y-0.5 hover:border-mg-accent/40 hover:shadow-sm'
                }`}
              >
                <div className="relative aspect-[5/3] w-full overflow-hidden bg-slate-950">
                  {showImage ? (
                    <>
                      <img
                        src={thumb}
                        alt=""
                        className="h-full w-full object-cover object-center transition-transform duration-200 group-hover:scale-[1.03]"
                        loading="lazy"
                        draggable={false}
                      />
                      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" />
                    </>
                  ) : (
                    <div className="flex h-full w-full flex-col items-center justify-center gap-1.5 bg-[radial-gradient(circle_at_30%_20%,#1e293b,#020617)]">
                      <Layers size={18} className="text-slate-600" aria-hidden />
                      <span className="text-[10px] font-medium text-slate-500">
                        {floor.hasBackground && loadingThumbnails ? 'Loading…' : 'Blank canvas'}
                      </span>
                    </div>
                  )}
                  {isSelected && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-mg-accent" aria-hidden />
                  )}
                </div>
                <div className="min-w-0 px-2 py-1.5">
                  <p
                    className={`line-clamp-2 min-h-[2rem] text-[11px] font-semibold leading-snug ${
                      isSelected ? 'text-mg-accent' : 'text-mg-text'
                    }`}
                  >
                    {floor.name}
                  </p>
                  <p className="mt-0.5 text-[10px] tabular-nums text-mg-muted">
                    {floor.widthM}×{floor.heightM} m
                  </p>
                </div>
              </button>
            )
          })}

          {onAddFloor && floors.length < 6 && (
            <button
              type="button"
              onClick={onAddFloor}
              className="flex min-w-[5.5rem] max-w-[7rem] flex-1 flex-col items-center justify-center gap-1 rounded-lg border border-dashed border-mg-border/80 bg-mg-page/50 px-2 py-3 text-mg-muted transition-colors hover:border-mg-accent/45 hover:bg-mg-accent-soft/30 hover:text-mg-accent"
              title="Add another floor"
            >
              <span className="flex h-9 w-9 items-center justify-center rounded-full border border-dashed border-current/40">
                <Plus size={16} />
              </span>
              <span className="text-[10px] font-semibold">New floor</span>
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
